import { action, computed, makeObservable, observable, toJS } from "mobx";
import { isEmpty, isObject, isString } from "lodash";
import { fetchProtectedResource, fetchResource } from "../../utilities";
import { message } from "antd";
import BaseService from "../base/BaseService";

const RELOAD_RULES_API_URL = "/api/v1/admin/reload-rules";
const SEND_RENEWAL_REMINDERS_API_URL = "/api/v1/admin/send-renewal-reminders";
const OAUTH_TOKEN_URL = "/oauth/token";
const WHO_AM_I_URL = "/api/v1/user/whoami";
const LOCAL_STORAGE_NAMESPACE = "app";

/**
 * MobX service containing application settings.
 */
class ApplicationService extends BaseService {
	/**
	 * Current app settings.
	 * @type {{}}
	 */
	@observable
	settings = {};

	/**
	 * Currently logged in user or null if anonymous.
	 * @type {{}}
	 */
	@observable
	userProfile = {};

	/**
	 * OAuth data, most importantly access_token
	 * @type {{}}
	 */
	@observable
	oAuthData = {};

	constructor(context) {
		super(context);
		const localStorage = this.context.localStorageService;

		localStorage.load(LOCAL_STORAGE_NAMESPACE, "settings", this.setSettings);
		localStorage.load(LOCAL_STORAGE_NAMESPACE, "oAuthData", (oAuthData) => {
			this.setOAuthData(oAuthData);

			this.whoAmI();
		});
		makeObservable(this);
	}

	isLocalStorageLoading = () => {
		return (
			this.context.localStorageService.currentlyLoading[
				LOCAL_STORAGE_NAMESPACE
			] &&
			this.context.localStorageService.currentlyLoading[LOCAL_STORAGE_NAMESPACE]
				.length > 0
		);
	};

	changeSetting = (key, value) => {
		this.setSetting(key, value);

		if (this.settings.sessionPersistence || key === "sessionPersistence") {
			this.context.localStorageService.save("settings", toJS(this.settings));
		}
	};

	setSettings = (settings) => {
		if (isObject(settings) && !isEmpty(settings)) {
			Object.keys(settings).forEach((k) => {
				this.setSetting(k, settings[k]);
			});
		}
	};

	@action
	setSetting = (k, v) => {
		this.settings[k] = v;
	};

	@action
	setUserProfile = (userProfile) => {
		this.userProfile = userProfile;
	};

	@action
	setOAuthData = (oAuthData) => {
		this.oAuthData = oAuthData;
	};

	@computed
	get hasToken() {
		return Object.entries(this.oAuthData).length > 0;
	}

	@computed
	get hasLoadedUserProfile() {
		return Object.entries(this.userProfile).length > 0;
	}

	@computed
	get isAuthenticated() {
		return this.hasToken && this.hasLoadedUserProfile;
	}

	/**
	 * Sends request to the server to reload insurance calculator rules.
	 * @returns {Promise<void>}
	 */
	reloadBusinessRules = async () => {
		let hide;
		try {
			hide = message.loading("Reloading rate files...", 0);
			await fetchProtectedResource(RELOAD_RULES_API_URL);
			hide();

			message.success("Rate files successfully reloaded!");
		} catch (error) {
			hide();
			message.error(
				`There was error while trying to reload rate files. Error: ${error}`
			);
		}
	};

	/**
	 * Sends request to the server to send all the renewal reminders.
	 * @returns {Promise<void>}
	 */
	sendRenewalReminders = async () => {
		let hide;
		try {
			hide = message.loading("Sending renewal reminders...", 0);
			await fetchProtectedResource(SEND_RENEWAL_REMINDERS_API_URL);
			hide();

			message.success("Reminders successfully sent!");
		} catch (error) {
			hide();
			message.error(
				`There was error while trying to send renewal reminders. Error: ${error}`
			);
		}
	};

	attemptUserLogin = async (email, password) => {
		let hideLoadingMessage;

		try {
			hideLoadingMessage = message.loading("Logging you in...", 0);

			let body = new URLSearchParams();
			body.set("grant_type", "password");
			body.set("username", email);
			body.set("password", password);
			body.set("client_id", "InsurForceWebApp");

			const oAuthData = await (
				await fetchResource(OAUTH_TOKEN_URL, {
					method: "POST",
					body,
				})
			).json();

			hideLoadingMessage();

			if (
				!isString(oAuthData.access_token) ||
				oAuthData.access_token.length < 1
			) {
				throw new Error(
					"Invalid response from OAuth authorization server: " +
						JSON.stringify(oAuthData)
				);
			}
			this.setOAuthData(oAuthData);
			this.whoAmI();
			this.context.localStorageService.save("oAuthData", toJS(this.oAuthData));
		} catch (error) {
			console.log(error);
			this.setOAuthData({});

			hideLoadingMessage();

			if (error.status === 401 || error.status === 400) {
				message.error(`Invalid credentials.`);
			} else if (error.status === 403) {
				message.error(`Unauthorized.`);
			} else {
				message.error(
					`There was error while trying to send data to the server. Error: ${error}`
				);
			}
		}
	};

	whoAmI = async () => {
		try {
			const userProfile = await (
				await fetchProtectedResource(WHO_AM_I_URL)
			).json();

			this.setUserProfile(userProfile);
		} catch (error) {
			console.log(error);
			this.setUserProfile({});

			// message.error(`There was error while loading your user profile. Please login again. Error: ${error}`);
		}
	};

	logout = () => {
		this.setOAuthData({});
		this.setUserProfile({});
		this.context.localStorageService.save("oAuthData", toJS(this.oAuthData));
	};
}

export default ApplicationService;
